import { useMutation, useQuery } from "@tanstack/react-query";
import { ArrowLeft, ArrowRight, Check, Loader2 } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import type {
  CreateOrderRequest,
  Department,
  DiscountType,
  OrderEnvelope,
  Profile,
} from "@tilt/pos-sdk";

import { getPosClient } from "../../lib/api";
import { formatCents, parseAmountToCents } from "../../lib/format";
import { cn } from "../../lib/utils";
import { Alert } from "../ui/Alert";
import { Card } from "../ui/Card";
import { CustomerLookup } from "./CustomerLookup";
import {
  EMPTY_CUSTOMER_LOOKUP,
  toCustomerOrderFields,
  type CustomerLookupValue,
} from "./customer-lookup-helpers";
import { presetForRow } from "./department-presets";
import { SaleStepIndicator } from "./SaleStepIndicator";

/**
 * Take-Payment wizard - three-step touch-friendly cashier flow that
 * opens a durable order before payment starts.
 *
 *   1. Department  - dropdown (single-select). Auto-skip when the
 *      location has zero or one dept, or when the cashier's per-
 *      membership default (#164) resolves a match.
 *   2. Reference   - single text field. The user either enters the
 *      dept's reference number ("RO #" / "PO #" / "Stock #") OR
 *      taps "I don't have one" to flag the order as exception
 *      (the back-office reconciles later).
 *   3. Customer + amount - customer details FIRST, amount LAST,
 *      mirroring the cashier's natural conversation order. Save +
 *      continue posts the order and hands off to the payment stage.
 *
 * The component is reusable so a future inventory-driven flow
 * (#135 dealer / vehicle inventory) can compose it with
 * pre-seeded ``initialDepartmentId`` + ``initialReference``
 * + ``initialCustomer`` and skip directly to step 3.
 *
 * Layout principles per the user's 2026-04-27 spec - works
 * equally well on touch (tablet) AND mouse + keyboard (PC):
 *   * No horizontal scroll.
 *   * No required vertical scroll on a 1024×768 tablet OR a
 *     desktop window down to ~720px tall.
 *   * 44px+ tap targets - equally comfortable for fingers and
 *     mouse clicks; doesn't feel cramped on PC.
 *   * Single-column form. Width capped to max-w-2xl so PC
 *     viewers don't get a sea-of-whitespace layout while tablet
 *     stays comfortable.
 *   * Enter key advances each step (PC ergonomics) without
 *     requiring tap on the Continue button.
 *   * autoFocus + visible focus ring on each step's primary
 *     input so a keyboard cashier can fly through.
 */

type Step = "department" | "reference" | "details";

/** Per-location sessionStorage key for the in-progress wizard draft.
 *  Bumped to v3 to add multi-discount array; stale v1/v2 drafts are
 *  silently discarded (the cashier just re-enters the order). */
const draftKey = (locationId: string): string => `tilt-pos.wizardDraft.${locationId}.v3`;

type DiscountMode = "amount" | "percent";

interface DraftDiscount {
  readonly tempId: string;
  readonly discountTypeId: string;
  readonly discountInput: string;
  readonly discountMode: DiscountMode;
  readonly discountReason: string;
}

function newDraftDiscount(): DraftDiscount {
  return {
    tempId: Math.random().toString(36).slice(2),
    discountTypeId: "",
    discountInput: "",
    discountMode: "amount",
    discountReason: "",
  };
}

interface WizardDraft {
  readonly step: Step;
  readonly departmentId: string;
  readonly reference: string;
  readonly noReference: boolean;
  readonly customer: CustomerLookupValue;
  readonly amountInput: string;
  readonly discounts: readonly DraftDiscount[];
}

/** Restore a draft from sessionStorage. Returns ``null`` for any read
 *  error, missing key, or shape mismatch - drafts are best-effort UX,
 *  never load-bearing. */
function loadWizardDraft(locationId: string): WizardDraft | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(draftKey(locationId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as WizardDraft;
    if (typeof parsed !== "object" || parsed === null) return null;
    return parsed;
  } catch {
    return null;
  }
}

export interface TakePaymentWizardProps {
  readonly profile: Profile;
  readonly locationId: string;
  /** Pre-fill the dept by id (rare - caller already knows the location's
   *  department uuid). */
  readonly initialDepartmentId?: string | null;
  /** Pre-fill the dept by canonical name (case-insensitive). The cashier-
   *  facing inventory CTA passes ``"sales"`` so vehicle take-deposits
   *  land on the Sales department even when the cashier's default
   *  membership-level dept is something else (Parts, Service, …).
   *  Resolved against the location's department list at mount. */
  readonly initialDepartmentName?: string;
  /** Pre-fill the reference (e.g. inventory passes the stock #). */
  readonly initialReference?: string;
  /** Pre-fill the customer (e.g. inventory has the buyer on file). */
  readonly initialCustomer?: CustomerLookupValue;
  /** Pre-fill the amount in cents (e.g. inventory has the listed
   *  price). NULL means the cashier types it in. */
  readonly initialTotalCents?: number | null;
  /** Pre-fill the order's description (V0058 - Inventory CTA passes
   *  the vehicle blurb so the back-office reviewer reading the order
   *  later doesn't have to cross-reference the inventory page). */
  readonly initialDescription?: string;
  readonly onOrderCreated: (envelope: OrderEnvelope) => void;
}

export function TakePaymentWizard(props: TakePaymentWizardProps) {
  const {
    profile,
    locationId,
    initialDepartmentId,
    initialDepartmentName,
    initialReference,
    initialCustomer,
    initialTotalCents,
    initialDescription,
    onOrderCreated,
  } = props;
  const { t } = useTranslation();

  // Restore a saved draft only when the caller is NOT explicitly
  // seeding fields. The Inventory take-deposit CTA passes
  // ``initialReference`` / ``initialDescription`` / etc. - that's a
  // fresh context the cashier just clicked into and a stale draft
  // from a previous /sale visit would clobber it. With no initial*
  // props the cashier landed on /sale fresh and we want to recover
  // their interrupted work (#5 from the UX review - phone calls,
  // sales-rep interruptions, kids on the showroom floor).
  const hasInitialSeed =
    Boolean(initialDepartmentId) ||
    Boolean(initialDepartmentName) ||
    Boolean(initialReference) ||
    Boolean(initialCustomer) ||
    initialTotalCents != null ||
    Boolean(initialDescription);
  // Read once on mount - we don't want the draft to flicker back if
  // sessionStorage updates from another tab.
  const restoredDraftRef = useRef<WizardDraft | null>(null);
  if (restoredDraftRef.current === null && !hasInitialSeed) {
    restoredDraftRef.current = loadWizardDraft(locationId);
  }
  const draft = restoredDraftRef.current;

  const departmentsQuery = useQuery({
    queryKey: ["pos", "departments", locationId],
    queryFn: () => getPosClient().listLocationDepartments(locationId),
    staleTime: 5 * 60_000,
  });
  const departments = useMemo(
    () => departmentsQuery.data?.departments ?? [],
    [departmentsQuery.data],
  );

  // The cascade for the initial dept:
  //   1. ``initialDepartmentId`` - caller already has the uuid.
  //   2. ``initialDepartmentName`` - caller knows the canonical name
  //      (e.g. inventory take-deposit passes "sales"). Resolved against
  //      the loaded department list, case-insensitive name match.
  //   3. Cashier's per-membership ``default_department_id`` (#164).
  //   4. Auto-pick when the location has exactly one dept.
  const deptByName = useMemo(() => {
    if (!initialDepartmentName) return null;
    const target = initialDepartmentName.trim().toLowerCase();
    if (!target) return null;
    return departments.find((d) => d.name.trim().toLowerCase() === target) ?? null;
  }, [departments, initialDepartmentName]);
  const defaultDeptId =
    initialDepartmentId ??
    deptByName?.id ??
    profile.active_membership?.default_department_id ??
    null;

  const [step, setStep] = useState<Step>(draft?.step ?? "department");
  const [departmentId, setDepartmentId] = useState<string>(draft?.departmentId ?? "");
  const [reference, setReference] = useState<string>(draft?.reference ?? initialReference ?? "");
  const [noReference, setNoReference] = useState<boolean>(draft?.noReference ?? false);
  // ``referenceLocked`` - the order number was supplied by an upstream
  // context (Inventory's "Take Deposit", a future DMS pull-in, etc.).
  // We treat it as authoritative: the cashier cannot edit it, the
  // "I don't have an order number" exception path is hidden, and the
  // wizard skips the reference step entirely. Multiple deposits CAN
  // share the same order number (V0043's
  // ``pos_order_external_ref_idx`` is a non-UNIQUE lookup index, not
  // a uniqueness constraint), so re-using the stock # for a follow-on
  // payment on the same vehicle is a feature, not a collision.
  const referenceLocked = Boolean(initialReference && initialReference.trim());
  // Seed the customer's ``description`` from ``initialDescription``
  // when the caller didn't already supply one. The Description input
  // on the customer step is bound to ``customer.description``; without
  // this fold-in, the inventory CTA's pre-filled vehicle blurb went
  // straight to the create-order payload but the cashier never saw it
  // in the form (and couldn't edit it before submit).
  const [customer, setCustomer] = useState<CustomerLookupValue>(() => {
    if (draft) return draft.customer;
    const base = initialCustomer ?? EMPTY_CUSTOMER_LOOKUP;
    if (base.description.trim() || !initialDescription?.trim()) return base;
    return { ...base, description: initialDescription };
  });
  const [amountInput, setAmountInput] = useState<string>(
    draft?.amountInput ?? (initialTotalCents != null ? (initialTotalCents / 100).toFixed(2) : ""),
  );
  const [discounts, setDiscounts] = useState<DraftDiscount[]>(
    draft?.discounts ? [...draft.discounts] : [],
  );
  const [error, setError] = useState<string | null>(null);

  const discountTypesQuery = useQuery({
    queryKey: ["pos", "discount-types"],
    queryFn: () => getPosClient().listDiscountTypes(),
    staleTime: 10 * 60_000,
  });
  const discountTypes = useMemo(
    () => discountTypesQuery.data?.discount_types ?? [],
    [discountTypesQuery.data],
  );

  // Persist the in-progress draft on every field change. Cheap (one
  // JSON.stringify on a tiny object); no debounce needed. Cleared on
  // successful order creation below.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const next: WizardDraft = {
      step,
      departmentId,
      reference,
      noReference,
      customer,
      amountInput,
      discounts,
    };
    try {
      window.sessionStorage.setItem(draftKey(locationId), JSON.stringify(next));
    } catch {
      // Storage quota / private mode - drafts are best-effort, swallow.
    }
  }, [locationId, step, departmentId, reference, noReference, customer, amountInput, discounts]);

  // Auto-resolve department + advance past the dept step. Runs once
  // per render of the dept list; ``advancedRef`` guards against
  // re-firing on later list updates.
  const advancedRef = useRef(false);
  useEffect(() => {
    if (advancedRef.current) return;
    if (departmentId) {
      // Draft restore (or other path) already set the dept - skip the
      // auto-resolve so we don't reset the cashier's restored ``step``.
      advancedRef.current = true;
      return;
    }
    if (departmentsQuery.isPending) return;
    if (departments.length === 0) {
      // Location has no depts configured - skip dept + reference
      // entirely and land on the details step. Reference becomes a
      // generic field with no label cue (the OrderStep doesn't
      // require dept_id).
      advancedRef.current = true;
      setStep("details");
      return;
    }
    const resolved =
      (defaultDeptId && departments.find((d) => d.id === defaultDeptId)) ||
      (departments.length === 1 ? departments[0] : null);
    if (resolved) {
      advancedRef.current = true;
      setDepartmentId(resolved.id);
      // When the reference came in pre-filled from an upstream context
      // (Inventory's Take Deposit, etc.), bypass the reference step
      // entirely and land on customer/amount. The number is locked,
      // so a step asking the cashier to type it is wasted taps.
      setStep(referenceLocked ? "details" : "reference");
    }
  }, [departmentsQuery.isPending, departments, defaultDeptId, departmentId, referenceLocked]);

  const selectedDept = useMemo(
    () => departments.find((d) => d.id === departmentId) ?? null,
    [departments, departmentId],
  );

  const referenceLabel = selectedDept?.reference_label || t("sale.reference");
  const referencePlaceholder = selectedDept?.reference_label ?? t("sale.referencePlaceholder");

  // Aggregate discount cents across all draft rows for the summary rail.
  const totalCentsForDiscount = parseAmountToCents(amountInput) ?? 0;
  const totalDiscountCents = discounts.reduce((sum, d) => {
    if (!d.discountInput.trim()) return sum;
    if (d.discountMode === "amount") return sum + (parseAmountToCents(d.discountInput) ?? 0);
    const pct = parseFloat(d.discountInput);
    if (!isFinite(pct) || pct <= 0 || pct > 100) return sum;
    const bps = Math.round(pct * 100);
    return sum + Math.floor((totalCentsForDiscount * bps) / 10000);
  }, 0);

  const submit = useMutation({
    mutationFn: async (): Promise<OrderEnvelope> => {
      const cents = parseAmountToCents(amountInput);
      if (cents == null || cents <= 0) {
        throw new Error(t("sale.errors.invalidAmount"));
      }
      // Validate each discount row — skip rows that are completely blank.
      for (const d of discounts) {
        const isBlank = !d.discountTypeId && !d.discountInput.trim();
        if (isBlank) continue;
        if (!d.discountTypeId)
          throw new Error(t("sale.discountType") + " " + t("sale.errors.required"));
        const dt = discountTypes.find((x) => x.id === d.discountTypeId);
        if (dt?.is_other && !d.discountReason.trim())
          throw new Error(t("sale.discountReasonRequired"));
        if (!d.discountInput.trim()) throw new Error(t("sale.errors.invalidAmount"));
        if (d.discountMode === "percent") {
          const pct = parseFloat(d.discountInput);
          if (!isFinite(pct) || pct <= 0 || pct > 100)
            throw new Error(t("sale.discountPercentInvalid"));
          // Validate the rounded value so inputs like 0.004% (bps=0) are rejected.
          if (Math.round(pct * 100) < 1) throw new Error(t("sale.discountPercentInvalid"));
        } else {
          const amt = parseAmountToCents(d.discountInput);
          if (amt == null || amt < 1) throw new Error(t("sale.errors.invalidAmount"));
        }
      }
      if (totalDiscountCents > cents) throw new Error(t("sale.discountTooLarge"));
      const customerFields = toCustomerOrderFields(customer);
      const discountPayload = discounts
        .filter((d) => d.discountTypeId && d.discountInput.trim())
        .map((d) => {
          const bps =
            d.discountMode === "percent"
              ? Math.round(parseFloat(d.discountInput) * 100)
              : undefined;
          const amount =
            d.discountMode === "amount"
              ? (parseAmountToCents(d.discountInput) ?? 0)
              : Math.floor((cents * (bps ?? 0)) / 10000);
          return {
            discount_type_id: d.discountTypeId,
            amount_cents: amount,
            ...(bps != null ? { percent_bps: bps } : {}),
            ...(d.discountReason.trim() ? { reason: d.discountReason.trim() } : {}),
          };
        });
      const payload: CreateOrderRequest = {
        location_id: locationId,
        total_cents: cents,
        ...(departmentId ? { department_id: departmentId } : {}),
        ...(reference.trim() && !noReference ? { external_reference: reference.trim() } : {}),
        ...(noReference ? { is_exception: true } : {}),
        ...customerFields,
        ...(discountPayload.length > 0 ? { discounts: discountPayload } : {}),
      };
      return getPosClient().createOrder(payload);
    },
    onSuccess: (envelope) => {
      // The order is created - the draft is no longer "in progress".
      // Clear it so a subsequent /sale visit starts fresh instead of
      // restoring this just-completed wizard.
      try {
        window.sessionStorage.removeItem(draftKey(locationId));
      } catch {
        // Best-effort.
      }
      onOrderCreated(envelope);
    },
    onError: (err) => setError(err instanceof Error ? err.message : String(err)),
  });

  if (departmentsQuery.isPending) {
    return (
      <Card>
        <p className="flex items-center gap-2 text-sm text-muted">
          <Loader2 className="h-4 w-4 animate-spin" /> {t("sale.loadingDepartments")}
        </p>
      </Card>
    );
  }

  // 2-column shell on desktop (work area on the left, contextual
  // ``OrderSummaryRail`` on the right that grows as the cashier
  // progresses). Stacks vertically on tablet + phone - the rail
  // reads as a "what you've entered so far" review at the bottom.
  // Department step still lets the tile grid breathe across the
  // full work-area column.
  return (
    <div className="grid w-full gap-6 lg:grid-cols-[minmax(0,42rem)_320px]">
      <div className="flex min-w-0 flex-col gap-4">
        <SaleStepIndicator
          step={step}
          hasDepartment={departments.length > 1}
          hasReference={!referenceLocked}
        />
        {error && (
          <Alert tone="error" title={t("sale.errors.title")}>
            {error}
          </Alert>
        )}

        {step === "department" && (
          <DepartmentStep
            departments={departments}
            value={departmentId}
            onChange={(id) => {
              setDepartmentId(id);
              setError(null);
            }}
            onPick={(id) => {
              setDepartmentId(id);
              setError(null);
              setStep("reference");
            }}
            onNext={() => {
              if (!departmentId) {
                setError(t("sale.errors.departmentRequired"));
                return;
              }
              setStep("reference");
            }}
          />
        )}

        {step === "reference" && (
          <ReferenceStep
            label={referenceLabel}
            placeholder={referencePlaceholder}
            value={reference}
            onChange={(v) => {
              setReference(v);
              setNoReference(false);
              setError(null);
            }}
            onBack={() => {
              if (departments.length > 1) setStep("department");
            }}
            showBack={departments.length > 1}
            onContinue={() => {
              if (!reference.trim()) {
                setError(t("sale.wizard.referenceRequired"));
                return;
              }
              setStep("details");
            }}
            onNoReference={() => {
              setReference("");
              setNoReference(true);
              setStep("details");
            }}
          />
        )}

        {step === "details" && (
          <DetailsStep
            customer={customer}
            onCustomer={setCustomer}
            amountInput={amountInput}
            onAmountChange={setAmountInput}
            discounts={discounts}
            onDiscountsChange={setDiscounts}
            discountTypes={discountTypes}
            totalDiscountCents={totalDiscountCents}
            totalCents={totalCentsForDiscount}
            referenceCleared={noReference}
            referenceLabel={referenceLabel}
            referenceValue={reference}
            onBack={() => {
              if (referenceLocked) {
                if (departments.length > 1) setStep("department");
                return;
              }
              setStep("reference");
            }}
            showBack={!referenceLocked || departments.length > 1}
            onSubmit={() => submit.mutate()}
            submitting={submit.isPending}
          />
        )}
      </div>
      <OrderSummaryRail
        step={step}
        department={selectedDept}
        referenceLabel={referenceLabel}
        reference={reference}
        noReference={noReference}
        customer={customer}
        amountInput={amountInput}
        discountCents={totalDiscountCents}
        discountLabel={
          selectedDept
            ? t("sale.discountFor", { department: selectedDept.name })
            : t("sale.discount")
        }
        // The summary's Edit chip jumps the cashier to the named step.
        // Block jumps to the reference step when locked - that step
        // wasn't part of this wizard's path and the rail's Edit
        // affordance shouldn't pretend it was.
        onJumpTo={(s) => {
          if (referenceLocked && s === "reference") return;
          setStep(s);
        }}
        referenceLocked={referenceLocked}
        hasDepartment={departments.length > 1}
      />
    </div>
  );
}

// ---------------------------------------------------------------- //
// Step 1 - Department dropdown                                     //
// ---------------------------------------------------------------- //

function DepartmentStep(props: {
  readonly departments: readonly Department[];
  readonly value: string;
  readonly onChange: (id: string) => void;
  /** Tile click: commit the id AND advance in one synchronous parent
   *  callback. Avoids the stale-closure race where ``onNext`` would
   *  read a still-empty ``departmentId`` if we called
   *  ``onChange(id) + requestAnimationFrame(onNext)`` instead. */
  readonly onPick: (id: string) => void;
  readonly onNext: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Card>
      <div className="flex flex-col gap-5 p-2">
        <div>
          <h2 className="text-lg font-semibold text-strong">{t("sale.wizard.departmentTitle")}</h2>
          <p className="mt-1 text-sm text-muted">{t("sale.wizard.departmentBody")}</p>
        </div>
        <div
          role="radiogroup"
          aria-label={t("sale.wizard.departmentLabel")}
          className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5"
        >
          {props.departments.map((d, i) => (
            <DepartmentTile
              key={d.id}
              dept={d}
              selected={props.value === d.id}
              autoFocus={i === 0 && !props.value}
              onPick={() => props.onPick(d.id)}
            />
          ))}
        </div>
        <div className="flex justify-end">
          <button
            type="button"
            onClick={props.onNext}
            disabled={!props.value}
            className="inline-flex min-h-[44px] items-center gap-2 rounded-md bg-brand-primary px-4 py-2 text-base font-semibold text-brand-primary-fg shadow-sm hover:opacity-90 disabled:opacity-50"
          >
            {t("sale.wizard.continue")}
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      </div>
    </Card>
  );
}

function DepartmentTile(props: {
  readonly dept: Department;
  readonly selected: boolean;
  readonly autoFocus: boolean;
  readonly onPick: () => void;
}) {
  const { dept, selected, autoFocus, onPick } = props;
  const preset = presetForRow(dept);
  const Icon = preset.Icon;
  // Top accent bar - preset.accent is always a hex string (raw from
  // catalog or vetted from the named-preset map). Inline-styled so
  // admins can brand departments without shipping a Tailwind config.
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={onPick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onPick();
        }
      }}
      autoFocus={autoFocus}
      className={cn(
        "group relative flex min-h-[132px] flex-col items-stretch overflow-hidden rounded-xl border bg-surface text-left shadow-sm transition",
        "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary",
        selected
          ? "border-brand-primary ring-2 ring-brand-primary ring-offset-2 ring-offset-surface"
          : "border-line hover:-translate-y-0.5 hover:border-line-strong hover:shadow-md",
      )}
    >
      <div className="h-1.5 w-full" style={{ backgroundColor: preset.accent }} aria-hidden="true" />
      <div className="flex flex-1 flex-col items-start gap-2 p-4">
        <div className="flex w-full items-center justify-between">
          <span
            className={cn(
              "inline-flex h-10 w-10 items-center justify-center rounded-lg",
              preset.iconBg,
              preset.iconFg,
            )}
            aria-hidden="true"
          >
            <Icon className="h-5 w-5" />
          </span>
          {selected && (
            <span
              className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-brand-primary text-brand-primary-fg"
              aria-hidden="true"
            >
              <Check className="h-3.5 w-3.5" />
            </span>
          )}
        </div>
        <span className="text-base font-semibold text-strong">{dept.name}</span>
        {dept.description && (
          <span className="line-clamp-2 text-xs text-muted">{dept.description}</span>
        )}
      </div>
    </button>
  );
}

// ---------------------------------------------------------------- //
// Step 2 - Reference (with "I don't have one" escape)              //
// ---------------------------------------------------------------- //

function ReferenceStep(props: {
  readonly label: string;
  readonly placeholder: string;
  readonly value: string;
  readonly onChange: (v: string) => void;
  readonly onBack: () => void;
  readonly showBack: boolean;
  readonly onContinue: () => void;
  readonly onNoReference: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-2">
        <h2 className="text-xl font-semibold tracking-tight text-strong sm:text-2xl">
          {t("sale.wizard.referenceTitle")}
        </h2>
        <p className="text-sm text-muted">
          {t("sale.wizard.referenceBody", { label: props.label })}
        </p>
      </div>

      <section className="rounded-xl bg-surface p-5 shadow-elev-1 sm:p-6">
        <label className="flex flex-col gap-3">
          <span className="text-[0.6875rem] font-semibold uppercase tracking-wider text-muted">
            {props.label}
          </span>
          <input
            type="text"
            value={props.value}
            onChange={(e) => props.onChange(e.target.value)}
            onKeyDown={(e) => {
              // Enter advances the step on PC; tap-tap on tablet.
              if (e.key === "Enter" && props.value.trim()) {
                e.preventDefault();
                props.onContinue();
              }
            }}
            placeholder={props.placeholder}
            aria-label={props.label}
            className="block w-full border-0 bg-transparent px-0 py-1 font-mono text-2xl font-semibold tracking-tight text-strong placeholder:text-faint focus:outline-none focus:ring-0 sm:text-3xl"
            autoFocus
            inputMode="text"
            autoComplete="off"
          />
        </label>
      </section>

      {/* On mobile, ``flex-col`` puts the no-reference escape hatch
          right under the input (where the cashier's eye is) and the
          Back/Continue buttons follow. Prior ``flex-col-reverse``
          buried the link below the action buttons - read as
          unrelated and easy to miss. (2026-05-03 mobile dogfood.) */}
      <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:items-center sm:justify-between">
        <button
          type="button"
          onClick={props.onNoReference}
          className="min-h-tap rounded-md text-sm font-medium text-muted underline-offset-2 hover:text-strong hover:underline"
        >
          {t("sale.wizard.noReferenceLink")}
        </button>
        <div className="flex gap-2">
          {props.showBack && (
            <button
              type="button"
              onClick={props.onBack}
              className="inline-flex min-h-tap items-center gap-2 rounded-md bg-surface px-4 text-sm font-medium text-foreground shadow-elev-1 transition hover:bg-surface-muted"
            >
              <ArrowLeft className="h-4 w-4" aria-hidden="true" />
              {t("sale.wizard.back")}
            </button>
          )}
          <button
            type="button"
            onClick={props.onContinue}
            disabled={!props.value.trim()}
            className="inline-flex min-h-tap items-center gap-2 rounded-md bg-primary px-5 text-sm font-semibold text-primary-foreground shadow-elev-1 transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {t("sale.wizard.continue")}
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- //
// Step 3 - Customer details + amount                               //
// ---------------------------------------------------------------- //

function DetailsStep(props: {
  readonly customer: CustomerLookupValue;
  readonly onCustomer: (next: CustomerLookupValue) => void;
  readonly amountInput: string;
  readonly onAmountChange: (next: string) => void;
  readonly discounts: DraftDiscount[];
  readonly onDiscountsChange: (next: DraftDiscount[]) => void;
  readonly discountTypes: readonly DiscountType[];
  readonly totalDiscountCents: number;
  readonly totalCents: number;
  readonly referenceCleared: boolean;
  readonly referenceLabel: string;
  readonly referenceValue: string;
  readonly onBack: () => void;
  readonly showBack: boolean;
  readonly onSubmit: () => void;
  readonly submitting: boolean;
}) {
  const { t } = useTranslation();

  // Deterministic focus management - replaces the previous (buggy)
  // dual-autoFocus pattern. Two cases hand focus to amount instead
  // of phone:
  //   1. Mount with a customer already attached (e.g., the inventory
  //      flow passes ``initialCustomer`` with a ``selectedCustomerId``).
  //      The phone field never renders, so without this nudge the
  //      cashier lands on a step with no focused input.
  //   2. CustomerLookup auto-attaches a single match while the
  //      cashier is still here - the phone field unmounts mid-step
  //      and focus snaps to ``document.body``. Re-route it to amount
  //      so the cashier's next keystroke types the dollar figure.
  // Phone field's own ``autoFocus`` still wins on first render when
  // no customer is attached.
  const amountInputRef = useRef<HTMLInputElement>(null);
  const wasAttachedRef = useRef<boolean>(props.customer.selectedCustomerId != null);
  useEffect(() => {
    const isAttached = props.customer.selectedCustomerId != null;
    if (isAttached && !wasAttachedRef.current) {
      amountInputRef.current?.focus();
    }
    wasAttachedRef.current = isAttached;
  }, [props.customer.selectedCustomerId]);
  // First-mount-with-attached path - runs only once, separate from
  // the transition effect so we don't re-focus on every render where
  // ``selectedCustomerId`` happens to be set.
  useEffect(() => {
    if (props.customer.selectedCustomerId != null) {
      amountInputRef.current?.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex flex-col gap-4">
      {/* The step indicator above already labels this step. Drop the
          duplicated h2 + body so the form fits above the fold on a
          768px-tall laptop at the typical Windows 125% scale (user
          feedback 2026-04-29 - Continue button was below the fold). */}
      {props.referenceCleared ? (
        <p className="inline-flex w-fit items-center gap-1.5 rounded-full bg-amber-50 px-3 py-1 text-xs font-medium text-amber-900 dark:bg-amber-900/30 dark:text-amber-200">
          {t("sale.wizard.exceptionPill")}
        </p>
      ) : props.referenceValue ? (
        <p className="inline-flex w-fit items-center gap-1.5 rounded-full bg-surface-muted px-3 py-1 text-xs font-medium text-foreground">
          {props.referenceLabel}: <span className="font-mono">{props.referenceValue}</span>
        </p>
      ) : null}

      {/* Customer panel. Phone first, then name + email side-by-side
          on tablet+, then description. CustomerLookup owns the per-
          field 2-col grid; we just give it the panel. */}
      <section className="rounded-xl bg-surface p-4 shadow-elev-1 sm:p-5">
        <p className="mb-3 text-[0.6875rem] font-semibold uppercase tracking-wider text-muted">
          {t("sale.wizard.customerSection")}
        </p>
        <CustomerLookup value={props.customer} onChange={props.onCustomer} />
      </section>

      {/* Discount panel — optional give-away on the order. Supports
          multiple stacked discount rows. Each row picks a type and
          enters a $ or % amount. Hidden when no discount types exist
          for this partner. */}
      {props.discountTypes.length > 0 && (
        <section className="rounded-xl bg-surface p-4 shadow-elev-1 sm:p-5">
          <div className="mb-3 flex items-center justify-between">
            <p className="text-[0.6875rem] font-semibold uppercase tracking-wider text-muted">
              {t("sale.discount")}
            </p>
          </div>
          <div className="flex flex-col gap-4">
            {props.discounts.map((d, idx) => {
              const dt = props.discountTypes.find((x) => x.id === d.discountTypeId) ?? null;
              const updateRow = (patch: Partial<DraftDiscount>) =>
                props.onDiscountsChange(
                  props.discounts.map((r, i) => (i === idx ? { ...r, ...patch } : r)),
                );
              return (
                <div
                  key={d.tempId}
                  className="flex flex-col gap-2 rounded-lg border border-line p-3"
                >
                  <div className="flex gap-2">
                    <select
                      value={d.discountTypeId}
                      onChange={(e) =>
                        updateRow({ discountTypeId: e.target.value, discountReason: "" })
                      }
                      className="min-h-tap flex-1 rounded-md border border-line bg-surface px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                      aria-label={t("sale.discountType")}
                    >
                      <option value="">{t("sale.discountType")}</option>
                      {props.discountTypes.map((x) => (
                        <option key={x.id} value={x.id}>
                          {x.display_name}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() =>
                        props.onDiscountsChange(props.discounts.filter((_, i) => i !== idx))
                      }
                      className="min-h-tap rounded-md px-2 text-sm text-muted hover:text-strong"
                      aria-label={t("sale.removeDiscount")}
                    >
                      &times;
                    </button>
                  </div>
                  {dt?.is_other && (
                    <input
                      type="text"
                      value={d.discountReason}
                      onChange={(e) => updateRow({ discountReason: e.target.value })}
                      placeholder={t("sale.discountReason")}
                      maxLength={200}
                      className="min-h-tap w-full rounded-md border border-line bg-surface px-3 text-sm text-foreground placeholder:text-faint focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  )}
                  {d.discountTypeId && (
                    <div className="flex items-center gap-2">
                      <div className="flex overflow-hidden rounded-md border border-line">
                        <button
                          type="button"
                          onClick={() => updateRow({ discountMode: "amount" })}
                          className={cn(
                            "min-h-tap px-3 text-sm font-medium transition",
                            d.discountMode === "amount"
                              ? "bg-primary text-primary-foreground"
                              : "bg-surface text-muted hover:text-foreground",
                          )}
                        >
                          {t("sale.discountModeAmount")}
                        </button>
                        <button
                          type="button"
                          onClick={() => updateRow({ discountMode: "percent" })}
                          className={cn(
                            "min-h-tap px-3 text-sm font-medium transition",
                            d.discountMode === "percent"
                              ? "bg-primary text-primary-foreground"
                              : "bg-surface text-muted hover:text-foreground",
                          )}
                        >
                          {t("sale.discountModePercent")}
                        </button>
                      </div>
                      <input
                        type="text"
                        value={d.discountInput}
                        onChange={(e) => updateRow({ discountInput: e.target.value })}
                        placeholder={d.discountMode === "amount" ? "0.00" : "0"}
                        inputMode="decimal"
                        className="min-h-tap w-28 rounded-md border border-line bg-surface px-3 font-mono text-sm text-foreground placeholder:text-faint focus:outline-none focus:ring-2 focus:ring-primary"
                        aria-label={t("sale.discount")}
                      />
                    </div>
                  )}
                </div>
              );
            })}
            <button
              type="button"
              onClick={() => props.onDiscountsChange([...props.discounts, newDraftDiscount()])}
              disabled={props.totalDiscountCents >= props.totalCents && props.totalCents > 0}
              className="min-h-tap w-full rounded-md border border-dashed border-line text-sm text-muted hover:border-primary hover:text-primary disabled:cursor-not-allowed disabled:opacity-40"
            >
              + {t("sale.addDiscount")}
            </button>
          </div>
        </section>
      )}

      {/* Amount panel. Compact label + input, plus the small hint
          line. Smaller than the prior hero block since the right-
          rail order-summary already echoes the amount as the cashier
          types - the form just needs a clean input, not a duplicated
          hero. Total vertical savings vs. the prior layout: ~120px,
          enough to bring Continue above the fold at 125% scale.

          NOTE: no ``autoFocus`` here - the cashier's first move is
          phone-number lookup, so initial focus belongs to
          ``CustomerLookup``'s phone field above (which sets its own
          ``autoFocus``). The ref-driven effect above takes over for
          the cases where phone wouldn't be rendered (initialCustomer
          pre-attached, or CustomerLookup auto-attaches a single match)
          so keyboard flow stays deterministic. */}
      <section className="rounded-xl bg-surface p-4 shadow-elev-1 sm:p-5">
        <label className="flex flex-col gap-2">
          <span className="text-[0.6875rem] font-semibold uppercase tracking-wider text-muted">
            {t("sale.wizard.amountLabel")}
          </span>
          <div className="flex items-baseline gap-2">
            <span className="font-mono text-2xl font-medium text-muted">$</span>
            <input
              ref={amountInputRef}
              type="text"
              value={props.amountInput}
              onChange={(e) => props.onAmountChange(e.target.value)}
              placeholder="0.00"
              inputMode="decimal"
              aria-label={t("sale.wizard.amountLabel")}
              className={cn(
                "block w-full bg-transparent font-mono text-3xl font-semibold tabular-nums tracking-tight text-strong placeholder:text-faint",
                "border-0 px-0 py-1 outline-none focus:outline-none focus:ring-0",
              )}
              autoComplete="off"
            />
          </div>
        </label>
      </section>

      {/* Action row pinned at the bottom of the work area. Primary
          (Save and continue) reads as the next-step affordance;
          Back is a quiet secondary. */}
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        {props.showBack && (
          <button
            type="button"
            onClick={props.onBack}
            disabled={props.submitting}
            className="inline-flex min-h-tap items-center gap-2 rounded-md bg-surface px-4 text-sm font-medium text-foreground shadow-elev-1 transition hover:bg-surface-muted disabled:opacity-50"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            {t("sale.wizard.back")}
          </button>
        )}
        <button
          type="button"
          onClick={props.onSubmit}
          disabled={props.submitting || !props.amountInput.trim()}
          aria-busy={props.submitting}
          className="inline-flex min-h-tap items-center justify-center gap-2 rounded-md bg-primary px-5 text-sm font-semibold text-primary-foreground shadow-elev-1 transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-90"
        >
          {props.submitting && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
          {/* Switch the label while the request is in flight so the
              cashier reads a clear "we're working on it" cue, not just
              a tiny spinner next to the unchanged Continue text. The
              layout shift from wizard step 3 to the collect view is
              abrupt; pre-flight label + opacity-90 disabled state +
              aria-busy gives a louder mid-state signal (2026-05-02
              dogfood - "nothing between 3 and 4 indicates something
              is happening"). */}
          {props.submitting ? t("sale.wizard.creatingOrder") : t("sale.wizard.saveAndContinue")}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- //
// Order Summary rail - context panel that grows as the cashier      //
// fills the wizard. On lg+ it sits to the right of the work area;   //
// on tablet/phone it stacks below the current step as a "what       //
// you've entered" review. Each filled section is a quick edit       //
// jump-back so the cashier can correct a typo without re-typing.    //
// ---------------------------------------------------------------- //

function OrderSummaryRail(props: {
  readonly step: Step;
  readonly department: Department | null;
  readonly referenceLabel: string;
  readonly reference: string;
  readonly noReference: boolean;
  readonly customer: CustomerLookupValue;
  readonly amountInput: string;
  readonly discountCents: number;
  readonly discountLabel: string;
  readonly hasDepartment: boolean;
  readonly onJumpTo: (step: Step) => void;
  /** True when the reference came in pre-supplied (e.g. Inventory's
   *  Take Deposit). Suppresses the row's Edit affordance - there's
   *  no reference step in this wizard path to jump back to. */
  readonly referenceLocked: boolean;
}) {
  const { t } = useTranslation();
  const cents = parseAmountToCents(props.amountInput);
  const netCents = cents != null ? Math.max(0, cents - props.discountCents) : null;
  const customerName =
    props.customer.name.trim() || props.customer.phone || props.customer.email || "";

  // Empty-state when the cashier hasn't picked anything yet - soft
  // hint that the panel will fill in as they progress, instead of a
  // confusing blank space.
  const hasAnything =
    !!props.department || !!props.reference.trim() || !!customerName.trim() || cents != null;

  return (
    // Hidden below lg - on mobile/tablet the rail stacks below the
    // active step and ate roughly half a phone viewport of vertical
    // room without showing anything the cashier didn't just type.
    // The active step's own form already shows what they entered;
    // the rail's value is the desktop's persistent right-rail review.
    // (2026-05-03 mobile dogfood.)
    <aside
      aria-label={t("sale.summary.title")}
      className="hidden lg:sticky lg:top-6 lg:flex lg:flex-col lg:gap-3 lg:self-start"
    >
      <div className="rounded-xl bg-surface p-5 shadow-elev-1">
        <p className="text-[0.6875rem] font-semibold uppercase tracking-wider text-muted">
          {t("sale.summary.title")}
        </p>
        {!hasAnything ? (
          <p className="mt-3 text-sm text-muted">{t("sale.summary.empty")}</p>
        ) : (
          // Plain ``div`` rather than ``dl`` - the rows aren't strict
          // term/description pairs (they include inline Edit buttons
          // and a hero amount block), so semantically a generic
          // container reads more honestly to assistive tech.
          <div className="mt-4 flex flex-col gap-4">
            {props.hasDepartment && (
              <SummaryRow
                label={t("sale.summary.department")}
                value={props.department?.name ?? null}
                onEdit={() => props.onJumpTo("department")}
                editLabel={t("sale.summary.edit")}
              />
            )}
            <SummaryRow
              label={props.referenceLabel}
              value={
                props.noReference ? t("sale.summary.exception") : props.reference.trim() || null
              }
              mono={!props.noReference}
              {...(props.referenceLocked ? {} : { onEdit: () => props.onJumpTo("reference") })}
              editLabel={t("sale.summary.edit")}
            />
            <SummaryRow
              label={t("sale.summary.customer")}
              value={customerName || null}
              {...(props.step === "details" ? {} : { onEdit: () => props.onJumpTo("details") })}
              editLabel={t("sale.summary.edit")}
            />
            <div className="border-t border-line pt-4">
              {props.discountCents > 0 && cents != null ? (
                <>
                  <div className="flex items-baseline justify-between text-sm">
                    <span className="text-[0.6875rem] font-semibold uppercase tracking-wider text-muted">
                      {t("sale.subtotal")}
                    </span>
                    <span className="font-mono font-medium text-foreground">
                      {formatCents(cents, "USD")}
                    </span>
                  </div>
                  <div className="mt-1 flex items-baseline justify-between text-sm text-emerald-600 dark:text-emerald-400">
                    <span className="text-[0.6875rem] font-semibold uppercase tracking-wider">
                      {props.discountLabel}
                    </span>
                    <span className="font-mono font-medium">
                      -{formatCents(props.discountCents, "USD")}
                    </span>
                  </div>
                  <div className="mt-2">
                    <p className="text-[0.6875rem] font-semibold uppercase tracking-wider text-muted">
                      {t("sale.totalDue")}
                    </p>
                    <p className="mt-1 font-mono text-3xl font-semibold tracking-tight text-strong">
                      {netCents != null ? formatCents(netCents, "USD") : "-"}
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-[0.6875rem] font-semibold uppercase tracking-wider text-muted">
                    {t("sale.summary.amount")}
                  </p>
                  <p className="mt-1 font-mono text-3xl font-semibold tracking-tight text-strong">
                    {cents != null ? formatCents(cents, "USD") : "-"}
                  </p>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </aside>
  );
}

function SummaryRow(props: {
  readonly label: string;
  readonly value: string | null;
  readonly mono?: boolean;
  readonly onEdit?: () => void;
  readonly editLabel: string;
}) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="text-[0.6875rem] font-semibold uppercase tracking-wider text-muted">
          {props.label}
        </p>
        <p
          className={cn(
            "mt-1 break-words text-sm font-medium text-strong",
            props.mono && "font-mono",
            !props.value && "italic text-faint",
          )}
        >
          {props.value ?? "-"}
        </p>
      </div>
      {props.onEdit && (
        <button
          type="button"
          onClick={props.onEdit}
          className="shrink-0 text-xs font-medium text-foreground hover:text-strong"
        >
          {props.editLabel}
        </button>
      )}
    </div>
  );
}
